import {
	ReactElement,
	useCallback,
	useRef,
	useState
} from "react";
import {
	HubCommentModel,
	HubCommentStatisticsModel,
	ReactionCounterModel,
	ReactionUserModel,
	ReactionKind
} from "client/Api/Hub/common";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import styles from "./CommentFooter.module.less";
import { ReactionsButton } from "client/Features/Reactions/ReactionsButton/ReactionsButton";
import { ReactionsCountButton } from "../../../Reactions/ReactionsCountButton/ReactionsCountButton";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import { TextAreaHandle, Toolbar } from "client/Components";
import { ReplySection } from "./ReplySection";
import { localize } from "client_legacy/ndaw/culture";
import { HubCommentSectionService } from "../HubCommentSection";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import { useReactionsDialog } from "client/Features/Reactions/ReactionsDialog/ReactionsDialog";

const MAX_NUMBER_OF_REACTIONS_ON_MOBILE: number = 2;

export type CommentFooterProps = {
	service: HubCommentSectionService;
	commentId: number;
	parentId: number | null;
	statistics: HubCommentStatisticsModel;
	onReactionChange: (
		newReaction: ReactionKind,
		commentId: number
	) => void;
	onReplyMobileComment: (
		parentCommentId: number,
		replyCommentId: number,
		authorUserInfo: UserIdentityModel
	) => void;
	authorUserInfo: UserIdentityModel;
	currentUserInfo: UserIdentityModel;
	options: HubCommentOptionsModel;
	onReplyCreated: (comment: HubCommentModel) => void;
};

export const CommentFooter = (props: CommentFooterProps): ReactElement => {
	const {
		service,
		commentId,
		parentId,
		statistics,
		onReactionChange,
		onReplyMobileComment,
		onReplyCreated,
		authorUserInfo,
		currentUserInfo,
		options
	} = props;

	const [showReply, setShowReply] = useState<boolean>(false);
	const textAreaRef = useRef<TextAreaHandle>(null);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const [reactionDialog, openReactionDialog] = useReactionsDialog();

	const reactionClickHandler = useCallback(
		(newReaction: ReactionKind): void => {
			if (newReaction === statistics.currentUserReaction) {
				onReactionChange(
					ReactionKind.NoReaction,
					commentId
				);
			} else {
				onReactionChange(
					newReaction,
					commentId
				);
			}
		},
		[
			onReactionChange,
			statistics,
			commentId
		]
	);

	const getCommentReactionCountsHandler = useCallback(
		(
			commentId: number,
			signal?: AbortSignal
		): Promise<readonly ReactionCounterModel[]> => {
			return service.getCommentReactionCounts(
				commentId,
				signal
			);
		},
		[service]
	);

	const getCommentReactionsHandler = useCallback(
		(
			commentId: number,
			pageSize: number,
			skip: number,
			kind: ReactionKind | undefined,
			signal?: AbortSignal
		): Promise<LazyLoadPageResult<ReactionUserModel>> => {
			return service.getCommentReactions(
				commentId,
				pageSize,
				skip,
				kind,
				signal
			);
		},
		[service]
	);

	const replyClickHandler = (): void => {
		if (isMobile) {
			onReplyMobileComment(
				parentId ?? commentId,
				commentId,
				authorUserInfo
			);

			return;
		}

		setShowReply(true);
		textAreaRef.current
			?.focus();
	};

	const onCancel = (): void => {
		setShowReply(false);
	};

	return (
		<footer>
			<div className={styles.wrapper}>
				<div className={styles.actions}>
					<Toolbar
						moreButtonKind="transparent-colored"
					>
						<Toolbar.Custom
							responsiveBehavior="always-visible"
							renderNormalMode={() => (
								<ReactionsButton
									userReaction={statistics.currentUserReaction}
									onReactionClick={reactionClickHandler}
								/>
							)}
							renderCollapsedMode={() => {
								throw new Error("Does not support collapse");
							}}
						/>
						<Toolbar.Button
							kind="transparent-for-list"
							text={localize("_Reply(Verb)")}
							icon="reply"
							onClick={replyClickHandler}
							trackingId="show-reply-input"
						/>
					</Toolbar>
				</div>

				{statistics.reactionsCount > 0 && (
					<ReactionsCountButton
						trackingId="reactions-count-button"
						counters={statistics.reactionTypes}
						maxAmountOfIconsToShow={
							isMobile
								? MAX_NUMBER_OF_REACTIONS_ON_MOBILE
								: undefined
						}
						onClick={() => openReactionDialog({
							itemId: statistics.commentId,
							getReactionCounts: getCommentReactionCountsHandler,
							getReactions: getCommentReactionsHandler
						})}
					/>
				)}
				{reactionDialog}
			</div>
			{showReply && (
				<div>
					<ReplySection
						service={service}
						currentUserInfo={currentUserInfo}
						onReplyCreated={onReplyCreated}
						commentId={commentId}
						commentAuthorUserInfo={authorUserInfo}
						parentId={parentId}
						onCancel={onCancel}
						ref={textAreaRef}
						options={options}
					/>
				</div>
			)}
		</footer>
	);
};
CommentFooter.displayName = "CommentFooter";
